import { Form, Modal, Row, Col, DatePicker, Select, Input, InputNumber, Spin, Typography } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CHALLAN_LIST,
  GET_SUPPLIER_ENDPOINT,
  INVOICE_BY_SUPPLIER,
  RAW_MATERIAL_INVENTORY_EDIT,
  RAW_MATERIAL_UPDATE,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import StoreRequisitionNoSelect from "common/StoreRequisitionNoSelect";
import { commaSeparateNumber } from "../../../utils/functions";

const { Text } = Typography;

const INCOMING_MOVEMENT_TYPES = [
  "receive_from_supplier",
  "receive_from_sub_con_factory",
  "return_from_cutting",
  "return_from_sewing",
  "return_from_folding",
  "return_from_packing",
  "assign_from_free_balance",
  "return_from_cutting_final",
  "return_from_buying_office",
  "return_from_cutting_loose",
  "return_from_cutting_final_loose",
];

const OUTGOING_MOVEMENT_TYPES = [
  "issue_to_cutting",
  "issue_to_sewing",
  "issue_to_folding",
  "issue_to_packing",
  "mark_as_free",
  "issue_to_sample",
  "send_to_buying_office",
  "issue_to_lab",
  "issue_to_cutting_final",
  "stock_sale",
  "return_to_supplier",
  "return_to_sub_con_factory",
  "disposal",
  "issue_to_others",
  "issue_to_cutting_loose",
  "issue_to_cutting_final_loose",
];

const normalizeTrackRows = (data) => {
  const raw = data?.track_item_histories ?? data?.track_item_history;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((th) => {
    const ti = th.track_item || {};
    const bin = ti.bins_info || {};
    const rack = bin.rack_info || {};
    const wh = rack.warehouse_info || {};
    return {
      history_id: th.id,
      track_id: th.track_id ?? ti.id,
      bin_id: ti.bin_id ?? bin?.id,
      warehouse_label: wh.name || "—",
      rack_label: rack.name || "—",
      bin_label: bin.name || "—",
      total_balance: Number(th.total_quantity ?? ti.total_quantity ?? 0),
      total_shipment: Number(th.shipment_qty ?? ti.shipment_qty ?? 0),
    };
  });
};

const FabricStoreEntryEditModal = ({ open, recordId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [challanList, setChallanList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const tracksWatch = Form.useWatch("tracks", form);
  const entryDateWatch = Form.useWatch("entry_date", form);

  const ensureSupplierInList = useCallback((data) => {
    const supplierId = Number(data?.supplier_id ?? data?.supplier_info?.id);
    const supplierName = data?.supplier_info?.name;
    if (!supplierId || !supplierName) return;

    setSupplierList((prev) => {
      const exists = prev.some((item) => Number(item?.id) === supplierId);
      if (exists) return prev;
      return [...prev, { id: supplierId, name: supplierName }];
    });
  }, []);

  const trackSums = useMemo(() => {
    const rows = Array.isArray(tracksWatch) ? tracksWatch : [];
    return rows.reduce(
      (acc, row) => {
        acc.totalBalance += Number(row?.total_balance ?? 0);
        acc.totalShipment += Number(row?.total_shipment ?? 0);
        return acc;
      },
      { totalBalance: 0, totalShipment: 0 },
    );
  }, [tracksWatch]);

  const movementType = detail?.movement_type || "";

  const actionLabel = useMemo(() => {
    if (INCOMING_MOVEMENT_TYPES.includes(movementType)) return "Receive";
    if (OUTGOING_MOVEMENT_TYPES.includes(movementType)) return "Issue";
    // fallback (in case new movement types are added)
    return movementType?.includes("receive") ? "Receive" : "Issue";
  }, [movementType]);

  const isReceiveFromSupplier = movementType === "receive_from_supplier";
  const isReceiveFromSubConFactory = movementType === "receive_from_sub_con_factory";
  const isReturnToSupplier = movementType === "return_to_supplier";
  const isReturnToSubConFactory = movementType === "return_to_sub_con_factory";
  const isIssueToLab = movementType === "issue_to_lab";
  const isSendToBuyingOffice = movementType === "send_to_buying_office";
  const isIssueToOthers = movementType === "issue_to_others";
  const isDisposal = movementType === "disposal";

  const incomingRequisitionRequiredTypes = [
    "return_from_cutting",
    "return_from_sewing",
    "return_from_folding",
    "return_from_finishing",
    "return_from_packing",
    "return_from_cutting_final",
    "return_from_cutting_loose",
    "return_from_cutting_final_loose",
  ];
  const outgoingRequisitionRequiredTypes = [
    "issue_to_cutting",
    "issue_to_sewing",
    "issue_to_folding",
    "issue_to_finishing",
    "issue_to_packing",
    "issue_to_sample",
    "issue_to_cutting_final",
    "issue_to_cutting_loose",
    "issue_to_cutting_final_loose",
  ];
  const showSupplier = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showInvoice = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showChallan = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showRequisition =
    isDisposal ||
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const showReferenceNo = isIssueToLab || isSendToBuyingOffice || isIssueToOthers;
  const showGatePass = isSendToBuyingOffice || isReturnToSubConFactory;
  const requireRequisition =
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const supplierFieldsDisabled = isReceiveFromSupplier && !entryDateWatch;
  const loadSuppliers = useCallback(async (name = "") => {
    const res = await getData(GET_SUPPLIER_ENDPOINT, false, { per_page: 100, name });
    if (res?.data?.code === 200) {
      setSupplierList(res?.data?.data?.data || []);
    }
  }, []);

  const loadChallans = useCallback(async (name = "") => {
    const res = await getData(CHALLAN_LIST, false, { per_page: 500, name });
    if (res?.data?.code === 200) {
      setChallanList(res?.data?.data?.data || []);
    }
  }, []);

  const loadInvoicesBySupplier = useCallback(async (supplierId) => {
    if (!supplierId) {
      setInvoiceList([]);
      return;
    }
    const res = await getData(`${INVOICE_BY_SUPPLIER}/${supplierId}`);
    if (res?.data?.code === 200) {
      setInvoiceList(res?.data?.data || []);
    }
  }, []);

  useEffect(() => {
    if (!open || !recordId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDetail(null);
      form.resetFields();

      try {
        const res = await getData(RAW_MATERIAL_INVENTORY_EDIT(recordId));
        if (cancelled) return;

        if (res?.data?.code !== 200) {
          HandleNotification("error", "bottomRight", res?.data?.message?.[0] || "Failed to load", null);
          onClose();
          return;
        }

        const data = res.data.data;
        setDetail(data);

        const tracks = normalizeTrackRows(data);
        form.setFieldsValue({
          entry_date: data.entry_date ? moment(data.entry_date, "YYYY-MM-DD") : undefined,
          supplier_id: data.supplier_id != null ? String(data.supplier_id) : undefined,
          invoice_id: data.invoice_id ?? undefined,
          challan_id: data.challan_id ?? undefined,
          requisition_no: data.requisition_no ?? undefined,
          reference_no: data.reference_no ?? undefined,
          gate_pass: data.gate_pass ?? undefined,
          received_total_quantity: Number(data.received_total_quantity) || 0,
          shipment_qty: Number(data.shipment_qty) || 0,
          tracks,
        });

        await Promise.all([loadSuppliers(), loadChallans()]);
        ensureSupplierInList(data);
        if (data.supplier_id) {
          await loadInvoicesBySupplier(data.supplier_id);
        } else {
          setInvoiceList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, recordId, form, loadSuppliers, loadChallans, loadInvoicesBySupplier, onClose, ensureSupplierInList]);

  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(tracksWatch) || tracksWatch.length === 0) return;

    const currentReceived = Number(form.getFieldValue("received_total_quantity") ?? 0);
    const currentShipment = Number(form.getFieldValue("shipment_qty") ?? 0);
    const nextReceived = Number(trackSums.totalBalance.toFixed(2));
    const nextShipment = Number(trackSums.totalShipment.toFixed(2));

    if (currentReceived !== nextReceived || currentShipment !== nextShipment) {
      form.setFieldsValue({
        received_total_quantity: nextReceived,
        shipment_qty: nextShipment,
      });
    }
  }, [open, tracksWatch, trackSums, form]);

  const onSupplierChange = (supplierId) => {
    form.setFieldsValue({ invoice_id: undefined });
    loadInvoicesBySupplier(supplierId);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!detail) return;

      setSaving(true);

      const locations =
        values.tracks?.map((row) => ({
          id: Number(row.history_id) || row.history_id,
          track_id: Number(row.track_id) || row.track_id,
          bin_id: Number(row.bin_id) || row.bin_id,
          shipment_qty: Number(row.total_shipment ?? 0),
          total_quantity: Number(row.total_balance ?? 0),
          convertion_unit_id: Number(row.convertion_unit_id) || detail.convertion_unit_id || detail.convertion_unit?.id || 1,
          convertion_rate:
            row.convertion_rate != null && row.convertion_rate !== ""
              ? Number(row.convertion_rate)
              : Number(detail.convertion_rate) || 1,
          is_delete: 0,
        })) ?? [];

      const bodyData = {
        movement_type: detail.movement_type,
        received_total_quantity: Number(values.received_total_quantity ?? 0),
        shipment_qty: Number(values.shipment_qty ?? 0),
        supplier_id: showSupplier ? values.supplier_id ?? null : null,
        invoice_id: showInvoice ? values.invoice_id ?? null : null,
        challan_id: showChallan ? values.challan_id ?? null : null,
        requisition_no: showRequisition ? values.requisition_no ?? null : null,
        reference_no: showReferenceNo ? values.reference_no ?? null : null,
        gate_pass: showGatePass ? values.gate_pass ?? null : null,
        entry_date: values.entry_date ? moment(values.entry_date).format("YYYY-MM-DD") : detail.entry_date,
        locations,
      };

      const response = await postData(`${RAW_MATERIAL_UPDATE}/${detail.id}`, bodyData);

      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", "Updated successfully", null);
        onSaved?.();
        onClose();
      } else {
        HandleNotification("error", "bottomRight", response?.message?.[0] || "Update failed", null);
      }
    } catch (e) {
      if (e?.errorFields) return;
      HandleNotification("error", "bottomRight", "Something went wrong", null);
    } finally {
      setSaving(false);
    }
  };

  const { Option } = Select;

  return (
    <Modal
      title="Edit fabric store entry"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      width={920}
      destroyOnClose
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {detail && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              {detail.style_info?.style_no && <>Style: {detail.style_info.style_no} · </>}
              {detail.inventory_info?.item_info?.name && <>{detail.inventory_info.item_info.name}</>}
            </Text>
          </div>
        )}

        <Form form={form} layout="vertical" size="small">
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Date"
                name="entry_date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current.endOf("day").isAfter(moment().endOf("day"))}
                />
              </Form.Item>
            </Col>
            {showSupplier && (
            <Col xs={24} sm={8}>
              <Form.Item label="Supplier" name="supplier_id" rules={[{ required: isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier, message: "Required" }]}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Supplier"
                  optionFilterProp="children"
                  onSearch={(v) => loadSuppliers(v)}
                  filterOption={false}
                  onChange={onSupplierChange}
                  disabled={supplierFieldsDisabled}
                >
                  {supplierList.map((s) => (
                    <Option key={s.id} value={String(s.id)}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showInvoice && (
            <Col xs={24} sm={8}>
              <Form.Item label="Invoice" name="invoice_id">
                <Select allowClear showSearch placeholder="Invoice" optionFilterProp="children" disabled={supplierFieldsDisabled}>
                  {invoiceList.map((inv) => (
                    <Option key={inv.id} value={inv.id}>
                      {inv.invoice_number || inv.invoice_no || inv.number || inv.id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showChallan && (
            <Col xs={24} sm={8}>
              <Form.Item label="Challan" name="challan_id">
                <Select
                  allowClear
                  showSearch
                  placeholder="Challan"
                  optionFilterProp="children"
                  onSearch={(v) => loadChallans(v)}
                  filterOption={false}
                  disabled={supplierFieldsDisabled}
                >
                  {challanList.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.challan_no || c.name || c.id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showRequisition && (
            <Col xs={24} sm={8}>
              <Form.Item
                label="Requisition No"
                name="requisition_no"
                className=""
                rules={[{ required: requireRequisition, message: "Required" }]}
              >
                <StoreRequisitionNoSelect movementType={movementType} />
              </Form.Item>
            </Col>
            )}
            {showReferenceNo && (
            <Col xs={24} sm={8}>
              <Form.Item
                label="Reference No"
                name="reference_no"
                rules={[{ required: isIssueToLab || isIssueToOthers || isSendToBuyingOffice, message: "Required" }]}
              >
                <Input size="small" placeholder="Reference no." />
              </Form.Item>
            </Col>
            )}
            {showGatePass && (
            <Col xs={24} sm={8}>
              <Form.Item label="Gate Pass" name="gate_pass" rules={[{ required: true, message: "Required" }]}>
                <Input size="small" placeholder="Gate pass" />
              </Form.Item>
            </Col>
            )}
            <Col xs={24} sm={8}>
              <Form.Item
                label={`${actionLabel} total qty`}
                name="received_total_quantity"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={`${actionLabel} Roll`}
                name="shipment_qty"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Warehouse Tracking
          </Typography.Title>

          <Form.List name="tracks">
            {(fields) =>
              fields.length === 0 ? (
                <Text type="secondary">No warehouse tracking for this entry.</Text>
              ) : (
                <>
                  {fields.map((field) => (
                  <Row gutter={8} key={field.key} style={{ marginBottom: 8 }} align="middle">
                    <Col span={0}>
                      <Form.Item name={[field.name, "history_id"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item name={[field.name, "track_id"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item name={[field.name, "bin_id"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item name={[field.name, "convertion_unit_id"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item name={[field.name, "convertion_rate"]} hidden>
                        <Input type="hidden" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={5}>
                      <Form.Item label="Warehouse" name={[field.name, "warehouse_label"]}>
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Form.Item label="Rack" name={[field.name, "rack_label"]}>
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={4}>
                      <Form.Item label="Bin" name={[field.name, "bin_label"]}>
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={5}>
                      <Form.Item label={`${actionLabel} in BOM Unit`} name={[field.name, "total_balance"]}>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                          parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Form.Item label={`${actionLabel} Roll`} name={[field.name, "total_shipment"]}>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                          parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  ))}
                </>
              )
            }
          </Form.List>
        </Form>
      </Spin>
    </Modal>
  );
};

export default FabricStoreEntryEditModal;
